import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, ArrowLeft, Sparkles, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import RatingModal from '../components/RatingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [canRateStatus, setCanRateStatus] = useState({});

  useEffect(() => {
    loadMatches();
  }, []);

  useEffect(() => {
    if (matches.length > 0) {
      checkRatingStatus();
    }
  }, [matches]);

  const checkRatingStatus = async () => {
    const token = localStorage.getItem('token');
    const statusPromises = matches.map(async ({ user }) => {
      try {
        const response = await axios.get(`${API}/ratings/can-rate/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return { userId: user.id, ...response.data };
      } catch (error) {
        return { userId: user.id, can_rate: false };
      }
    });
    
    const statuses = await Promise.all(statusPromises);
    const statusMap = {};
    statuses.forEach(status => {
      statusMap[status.userId] = status;
    });
    setCanRateStatus(statusMap);
  };

  const loadMatches = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatches(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des matchs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRating = (match, user, e) => {
    e.stopPropagation();
    setSelectedMatch({ match, user });
    setRatingModalOpen(true);
  };

  const handleRatingSuccess = () => {
    checkRatingStatus();
    loadMatches();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/50 rounded-full"
            style={{ transition: 'background 0.2s' }}
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-3xl font-bold gradient-text">Mes Matchs</h1>
          <div className="w-10" />
        </div>

        {matches.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Heart className="w-20 h-20 mx-auto mb-6 text-pink-500" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Aucun match pour le moment</h2>
            <p className="text-gray-600 mb-8">Commencez à swiper pour trouver des matchs !</p>
            <button
              onClick={() => navigate('/discover')}
              className="btn-primary px-8 py-4"
            >
              Découvrir des profils
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map(({ match, user }) => (
              <div
                key={match.id}
                data-testid={`match-card-${user.id}`}
                className="glass-card overflow-hidden hover:scale-105 cursor-pointer"
                style={{ transition: 'transform 0.2s' }}
                onClick={() => navigate(`/chat/${match.id}`)}
              >
                <div className="relative aspect-square">
                  {user.photos && user.photos.length > 0 ? (
                    <img
                      src={`${API}/profile/photos/${user.photos[0]}`}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-pink-300 to-orange-300 flex items-center justify-center">
                      <Heart className="w-16 h-16 text-white opacity-50" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full">
                    <span className="text-sm font-bold text-pink-600">
                      {Math.round(match.affinity_score)}%
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{user.name}</h3>
                      <p className="text-sm text-gray-600">{user.age} ans • {user.location}</p>
                      {/* Social Score */}
                      {user.social_score && user.social_score.total_ratings > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="text-sm font-semibold text-gray-700">{user.social_score.average}</span>
                          <span className="text-xs text-gray-500">({user.social_score.total_ratings})</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {match.match_reason}
                  </p>

                  {user.interests && user.interests.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {user.interests.slice(0, 3).map(interest => (
                        <span
                          key={interest}
                          className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs"
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    data-testid={`chat-btn-${user.id}`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-lg font-medium hover:shadow-lg mb-2"
                    style={{ transition: 'box-shadow 0.2s' }}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Discuter
                  </button>

                  {/* Rating Button */}
                  {canRateStatus[user.id]?.can_rate && (
                    <button
                      onClick={(e) => handleOpenRating(match, user, e)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-pink-500 text-pink-600 rounded-lg font-medium hover:bg-pink-50"
                      style={{ transition: 'background 0.2s' }}
                    >
                      <Star className="w-4 h-4" />
                      Noter ce profil
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {ratingModalOpen && selectedMatch && (
        <RatingModal
          user={selectedMatch.user}
          matchId={selectedMatch.match.id}
          onClose={() => setRatingModalOpen(false)}
          onSuccess={handleRatingSuccess}
        />
      )}
    </div>
  );
}
